import React from "react";
import EmailViewPort from "../../../components/emails";
import useMailMessageControls from "../../../hooks/useMailMessageControls";
import useGetMailMessageByGraphId from "../hooks/useGetMailMessageByGraphId";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";
import { openFileEmailModal } from "../../../components/modals/email-file";
import { usePersonalOutlookContext } from "../../../hooks/PersonalOutlookContext";
import { useUnFileMailMutation } from "../../../services/useremails";
import { EmailAttachment, EmailDisplayContents } from "../../../components/emails/types";
import { convertMimeTypeToDocumentType } from "../../../helpers/fileHelpers";
import toast from "react-hot-toast";

const Email = () => {
    const {
        delegateMailUser,
        mailId,
        searchTerm,
        selectedMailUserId,
        selectedFolderId
    } = usePersonalOutlookContext();
    const { data, error, isLoading } = useGetMailMessageByGraphId(selectedMailUserId, selectedFolderId, mailId);
    const { onForward, onReply, onReplyAll } = useMailMessageControls(
        data,
        delegateMailUser,
        { folderId: selectedFolderId, searchTerm }
    );
    const [unFileMailTrigger, unFileMailTriggerResult] = useUnFileMailMutation();

    const handleOnFile = (email: EmailDisplayContents) : void => {
        openFileEmailModal(email, delegateMailUser, { folderId: selectedFolderId, searchTerm }).then();
    };

    const handleOnForward = () => onForward(null);

    const handleOnOpenAttachment = (attachment: EmailAttachment) => {
        const downloadableAttachment = data?.attachments?.find(e => e.id === attachment.id);
        if (!downloadableAttachment) {
            return;
        }

        const bytes = Uint8Array.from(window.atob(downloadableAttachment!.contentBytes!), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: downloadableAttachment!.contentType! });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = blobUrl;
        link.download = downloadableAttachment!.name;

        document.body.appendChild(link);

        link.dispatchEvent(
            new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
            })
        );

        document.body.removeChild(link);
    }

    const handleOnReply = () => onReply(null);

    const handleOnReplyAll = () => onReplyAll(null);

    const handleOnUnFile = (email: EmailDisplayContents) : void => {
        openConfirmModal({
            dialogTitle: "Un-file Email?",
            message: "Are you sure you want to un-file this email?",
            confirmButtonText: "Yes, Un-file",
            confirmButtonVariant: "danger",
            cancelButtonText: "Cancel"
        }).then(
            (r) => {
                if (r !== true) {
                    return;
                }
                toast.promise(unFileMailTrigger({
                    mailId: email!.id,
                    folderId: selectedFolderId,
                    searchTerm
                }), {
                    loading: "Removing email from file...",
                    error: (err) => err?.data?.message ?? "Unable to remove filed email. Please try again later.",
                    success: `Filed email has been removed.`
                }).then(() => {});
            }
        )
    };

    return (
        <EmailViewPort
            allowFiling={true}
            commands={{
                onFile: handleOnFile,
                onForward: handleOnForward,
                onOpenAttachment: handleOnOpenAttachment,
                onReply: handleOnReply,
                onReplyAll: handleOnReplyAll,
                onUnFile: handleOnUnFile
            }}
            email={{
                attachments: data?.attachments?.filter(e => !e.isInline).map((e) => ({
                    id: e.id ?? "",
                    name: e.name ?? "",
                    size: e.size ?? 0,
                    type: convertMimeTypeToDocumentType(e.contentType ?? "")
                } as EmailAttachment)) ?? [],
                bccRecipients: data?.bccRecipients?.map(el => el.emailAddress.address) ?? [],
                body: data?.body?.content ?? "",
                ccRecipients: data?.ccRecipients?.map(el => el.emailAddress.address) ?? [],
                conversationId: data?.conversationId ?? "",
                fromEmail: data?.from?.emailAddress?.address ?? "",
                id: data?.id ?? "",
                isFiled: data?.isFiled ?? false,
                isAssignedToProject: data?.isAssignedToProject ?? false,
                masterAccountId: data?.masterAccountId ?? null,
                projectId: data?.projectId ?? null,
                sentDate: data?.sentDateTime ?? "",
                subject: data?.subject ?? "",
                toRecipients: data?.toRecipients?.map(el => el.emailAddress.address) ?? [],
            }}
            loadOptions={{
                error,
                isLoading
            }}
            showProjectSelect={false}
        />
    )
};

export default Email;